import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../hook';
import { fetchCustomers, fetchDevices, fetchEvents } from './_slice';
import { Button, Card, Col, Modal, Row, Space, Switch, Table, TableProps, Tag } from 'antd';
import { Link, useParams } from 'react-router-dom';
import DeviceSearchForm from './features/device_search_form';
import { appConfig } from '../../common/config';
import LayoutStandard from '../../layouts/layout_standard';
import { EnumDeviceSearchType, IDeviceSearchParams } from '../../common/models';
import CopyToClipboard from '../../components/copy_clipboard';
import { compareTime, formatDateTime } from '../../common/utils';
import DeviceInstallation from '../device/install/device_installation';
import './styles.css';
import DevicesDeactivation from '../device/deactivate/devices_deactivation';
import CustomizedGoogleMapMulti from '../../components/google_map_multi';
import { getDeviceMarkerState, isPeopleSenseDevice } from '../../common/helper';
import DeviceFilterForm from './features/device_filter_form';

const fixedLeftColumns: TableProps<any>['columns'] = [
  {
    title: 'Device ID',
    dataIndex: 'DeviceId',
    key: 'DeviceId',
    width: 320,
    render: (val, record) => <>
      <span style={{color: record.DeactivationTime ? '#cccccc' : 'inherit'}}>{val}</span> <CopyToClipboard content={val} />
    </>,
    responsive: ['lg'],
  },
  {
    title: 'Device Serial',
    dataIndex: 'SerialNumber',
    key: 'SerialNumber',
    width: 150,
    sorter: (a, b) => a.SerialNumber > b.SerialNumber ? 1 : -1,
    render: (val, record) => <>
      <Link to={`/events/searchType/Device ID or Serial/keywords/${val}`}>
        <span style={{color: record.DeactivationTime ? '#cccccc' : 'inherit'}}><b>{val}</b></span>
      </Link> <CopyToClipboard content={val} />
    </>,
  },
  {
    title: 'Device Name',
    dataIndex: ['InstallationData', 'AdditionalData', 'Name'],
    key: 'DeviceName',
    render: (val, record) => <>
      <span style={{color: record.DeactivationTime ? '#cccccc' : 'inherit'}}><b>{val}</b></span> <CopyToClipboard content={val} />
      </>,
    sorter: (a, b) => {
      const nameA = a.InstallationData?.AdditionalData?.Name || '';
      const nameB = b.InstallationData?.AdditionalData?.Name || '';
      // First click the header: Ensure empty names are sorted to the end
      // Second click the header: Ensure empty names are sorted to the beginning
      if (nameA === '') return 1;
      if (nameB === '') return -1;
      return nameA.localeCompare(nameB);
    },
  },
];

const fixedRightColumns: TableProps<any>['columns'] = [
  {
    title: 'Device Type',
    dataIndex: 'DeviceTypeName',
    key: 'DeviceTypeName',
    width: 180,
    sorter: (a, b) => a.DeviceTypeName > b.DeviceTypeName ? 1 : -1,
  },
  {
    title: 'Network ID',
    dataIndex: 'NetworkId',
    key: 'NetworkId',
    render: (_, record) => (
      <>
        <Tag color={appConfig.getNetworkType(record.NetworkType).color}>{record.NetworkType}</Tag>
        {record.NetworkId} <CopyToClipboard content={record.NetworkId} />
      </>
    ),
    sorter: (a, b) => a.NetworkId > b.NetworkId ? 1 : -1,
  },
  {
    title: 'Last Received',
    dataIndex: 'LastReceived',
    key: 'LastReceived',
    render: formatDateTime,
    sorter: (a, b) => compareTime(a.LastReceived, b.LastReceived),
  },
  {
    title: 'Battery',
    dataIndex: 'BatteryVoltage',
    key: 'BatteryVoltage',
    render: (_, record) => {
      const voltage = record.Measurements?.['Battery Voltage'];
      const status = appConfig.getBatteryStatus(voltage);
      return <Tag color={status.color}>{status.text}</Tag>;
    },
    sorter: (a, b) => {
      const aVoltage = a.Measurements?.['Battery Voltage'];
      const bVoltage = b.Measurements?.['Battery Voltage'];
      if (aVoltage === undefined && bVoltage === undefined) return 0;
      if (aVoltage === undefined) return 1;
      if (bVoltage === undefined) return -1;
      return aVoltage > bVoltage ? -1 : 1;
    },
  },
];

interface RouteParams {
  searchType: string;
  keywords: string;
  [key: string]: string;
}

interface IDevicesPageProps {
}

const DevicesPage: React.FC<IDevicesPageProps> = () => {
  const customerState = useAppSelector((state) => state.customer);
  const dispatch = useAppDispatch();
  const { searchType, keywords } = useParams<RouteParams>();
  const [isInstallDeviceOpen, setIsInstallDeviceOpen] = useState(false);
  const [isDeactivateDevicesOpen, setIsDeactivateDevicesOpen] = useState(false);
  const [deviceSerial, setDeviceSerial] = useState<string>('');
  const [filteredKeywords, setFilteredKeywords] = useState<string>('');
  const [syncWithMap, setSyncWithMap] = useState<boolean>(true);
  const [showDeactivatedDevices, setShowDeactivatedDevices] = useState<boolean>(false);

  const eventsMap = customerState.events.reduce((map, event) => {
    const sn = event.Device.SerialNumber;
    if (!map[sn]) map[sn] = event;
    return map;
  }, {} as { [deviceId: string]: any });

  const devices = showDeactivatedDevices ? customerState.devices : customerState.devices.filter(d => !d.DeactivationTime);

  const allDevices = devices.map(x => ({
    ...x,
    coord: x.InstallationData?.Latitude && x.InstallationData?.Longitude
      ? { lat: x.InstallationData.Latitude, lng: x.InstallationData.Longitude }
      : undefined,
    title: x.SerialNumber,
    ...getDeviceMarkerState(eventsMap, x.SerialNumber, isPeopleSenseDevice(x)),
  }));

  const filteredDevicesForList = filteredKeywords?.length > 0
    ? allDevices.filter(device => {
      const keywords = filteredKeywords.trim().toLowerCase();
      return device.SerialNumber.toLowerCase().includes(keywords)
        || device.DeviceId.toLowerCase().includes(keywords)
        || device.InstallationData?.AdditionalData?.Name?.toLowerCase().includes(keywords)
        || device.InstallationData?.AdditionalData?.Address?.toLowerCase().includes(keywords)
        || device.NetworkId.toLowerCase().includes(keywords)
        || device.DeviceTypeName.toLowerCase().includes(keywords);
    })
    : allDevices;
  
  const filteredDevicesForMap = (syncWithMap ? filteredDevicesForList : allDevices).filter(device => device.coord !== undefined);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchDevices({ searchType: searchType as EnumDeviceSearchType, keywords }));
    dispatch(fetchEvents({ searchType: searchType as EnumDeviceSearchType, keywords }));
  }, [searchType, keywords, dispatch]);

  const openInstallDevice = (deviceSerial: string) => {
    setDeviceSerial(deviceSerial);
    setIsInstallDeviceOpen(true);
  };

  const closeInstallDevice = () => {
    setIsInstallDeviceOpen(false);
  };

  const openDeactivateDevices = () => {
    setIsDeactivateDevicesOpen(true);
  };

  const closeDeactivateDevices = () => {
    setIsDeactivateDevicesOpen(false);
  };

  const onSearch = (params: IDeviceSearchParams) => {
    dispatch(fetchDevices(params));
    dispatch(fetchEvents(params));
  };

  const onFilter = (keywords: string, syncWithMap: boolean) => {
    setFilteredKeywords(keywords);
    setSyncWithMap(syncWithMap);
  };

  const columns: TableProps<any>['columns'] = [
    {
      title: 'Position',
      key: 'position',
      render: (_, record) => (
        record.coord && <img
          src={record.icon}
          alt='Click to filter'
          style={{ width: 16, height: 'auto', verticalAlign: 'middle', cursor: 'pointer' }}
          onClick={() => setFilteredKeywords(record.SerialNumber)}
        />
      ),
      width: 30,
    },
    ...fixedLeftColumns,
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button type="primary" onClick={() => openInstallDevice(record.SerialNumber)} style={{ padding: 8 }}>Install</Button>
        </Space>
      ),
    },
    ...fixedRightColumns,
  ];

  return (
    <LayoutStandard page={appConfig.pages.devices} count={filteredDevicesForList.length}>
      <Space direction='vertical' size="middle" style={{ display: 'flex' }}>
        <Row gutter={24}>
          <Col xs={24} sm={24} md={24} lg={18}>
            <Card size='small'>
              <DeviceSearchForm
                searchType={searchType as EnumDeviceSearchType}
                keywords={keywords}
                customers={customerState.customers}
                onSearch={onSearch}
              />
            </Card>
          </Col>
          <Col xs={24} sm={24} md={24} lg={6} className='deactivate-card'>
            <Card size='small' style={{ padding: 10 }}>
              <Space size="small">
                <Button type="primary" onClick={openDeactivateDevices}>Deactivate Devices</Button>
                <Switch
                  checkedChildren="Show Deactivated Devices"
                  unCheckedChildren="Show Deactivated Devices"
                  onChange={(checked) => setShowDeactivatedDevices(checked)}
                />
              </Space>
            </Card>
          </Col>
        </Row>

        <Card size="default" variant="borderless">
          <CustomizedGoogleMapMulti
            devices={filteredDevicesForMap}
          />
        </Card>

        <Row gutter={24}>
          <Col xs={24} sm={24} md={24} lg={24}>
            <Card size='small'>
              <DeviceFilterForm keywords={filteredKeywords} onFilter={onFilter} />
            </Card>
          </Col>
        </Row>

        <Table<any>
          columns={columns}
          dataSource={filteredDevicesForList}
          rowKey='DeviceId'
          pagination={{ position: ['bottomCenter'] }}
          loading={customerState.isLoadingDevices}
          scroll={{ x: 'max-content' }}
        />
      </Space>
      <Modal
        title='Install Device'
        open={isInstallDeviceOpen}
        onCancel={closeInstallDevice}
        footer={null}
        width={'96%'}
        // If destroyOnClose is true, the modal is destroyed when closed; otherwise, it will be just hidden.
        // Keeping it not destoryed may cause unexpected behaviors.
        destroyOnClose={true}
        style={{ top: 10, maxWidth: 1600 }}
        className='install-device-modal'
      >
        <DeviceInstallation
          deviceIdOrSerial={deviceSerial}
          onSaved={() => {closeInstallDevice()}}
          onCanceled={() => {closeInstallDevice()}}
        />
      </Modal>
      <Modal
        title='Deactivate Devices'
        open={isDeactivateDevicesOpen}
        onCancel={closeDeactivateDevices}
        footer={null}
        width={'96%'}
        // If destroyOnClose is true, the modal is destroyed when closed; otherwise, it will be just hidden.
        // Keeping it not destoryed may cause unexpected behaviors.
        destroyOnClose={true}
        style={{ top: 10, maxWidth: 1600 }}
        className='install-device-modal'
      >
        <DevicesDeactivation />
      </Modal>
    </LayoutStandard>
  );
};

export default DevicesPage;

